/**
 * Secrets phase 1 (decision 0141), pure:
 *
 * - `secretsReaching` / `secretUnmet`: which secrets reach a run (its project and the person it works for), and why a
 *   named one does not. The hub's placement, the context packs and the delivery all read the same rule.
 * - `makeSecretRedactor`: exact-value redaction. Minga knows every value it hands out, so it removes exactly those (and
 *   their base64, URL- and JSON-encoded forms, and each long line of a multi-line value) from text before it is stored.
 *   `scrubSecrets` (pattern-based) stays behind it as a backstop for values Minga never saw.
 */
import type { SecretReachRow } from "@minga/protocol"
import { utf8 } from "./hash"

/** What a redacted value becomes: the secret's name, never its value. */
export const secretMark = (name: string): string => `«secret:${name}»`

/** Shorter forms are not redacted: they would blank ordinary words (a value is at least this long to be found). */
export const SECRET_MIN_FORM = 8
/** A line of a multi-line value (a PEM body line is 64 chars) is redacted on its own from this length. */
const SECRET_MIN_LINE = 16

const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/"
/** Standard base64 (with padding) of the text's UTF-8 bytes; core has no `btoa`. */
export const base64Utf8 = (text: string): string => {
  const bytes = utf8(text)
  let out = ""
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] as number
    const b = bytes[i + 1]
    const c = bytes[i + 2]
    out += B64[a >> 2]
    out += B64[((a & 3) << 4) | ((b ?? 0) >> 4)]
    out += b === undefined ? "=" : B64[((b & 15) << 2) | ((c ?? 0) >> 6)]
    out += c === undefined ? "=" : B64[c & 63]
  }
  return out
}
const b64 = base64Utf8

/** The forms of one value that may show up in text: as is, trimmed, encoded, and each long line. */
export const secretForms = (value: string): ReadonlyArray<string> => {
  const out = new Set<string>()
  const add = (v: string | null | undefined) => {
    if (v && v.length >= SECRET_MIN_FORM) out.add(v)
  }
  for (const v of new Set([value, value.trim()])) {
    if (v.length < SECRET_MIN_FORM) continue
    add(v)
    const std = b64(v)
    add(std)
    add(std.replace(/=+$/, ""))
    add(std.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""))
    add(encodeURIComponent(v))
    add(JSON.stringify(v).slice(1, -1))
  }
  if (value.includes("\n"))
    for (const line of value.split(/\r?\n/)) {
      const l = line.trim()
      if (l.length >= SECRET_MIN_LINE && !/^-----(BEGIN|END) /.test(l)) add(l)
    }
  return [...out]
}

export interface SecretRedactor {
  /** How many forms it removes (0: it changes nothing). */
  readonly size: number
  text(s: string): string
  /** Every string leaf of a JSON-like value, redacted; the structure and keys unchanged. */
  value<T>(v: T): T
}

/** A redactor for these values. Longer forms go first, so a value inside another is not cut in half. */
export const makeSecretRedactor = (
  entries: ReadonlyArray<{ readonly name: string; readonly value: string }>,
): SecretRedactor => {
  const forms: Array<{ form: string; mark: string }> = []
  const seen = new Set<string>()
  for (const e of entries)
    for (const form of secretForms(e.value))
      if (!seen.has(form)) {
        seen.add(form)
        forms.push({ form, mark: secretMark(e.name) })
      }
  forms.sort((a, b) => b.form.length - a.form.length)
  const text = (s: string): string => {
    if (forms.length === 0 || s.length < SECRET_MIN_FORM) return s
    let out = s
    for (const f of forms) if (out.includes(f.form)) out = out.split(f.form).join(f.mark)
    return out
  }
  const walk = (x: unknown): unknown => {
    if (typeof x === "string") return text(x)
    if (Array.isArray(x)) {
      let changed = false
      const next = x.map((v) => {
        const w = walk(v)
        if (w !== v) changed = true
        return w
      })
      return changed ? next : x
    }
    if (x && typeof x === "object") {
      let changed = false
      const next: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(x)) {
        const w = walk(v)
        if (w !== v) changed = true
        next[k] = w
      }
      return changed ? next : x
    }
    return x
  }
  return {
    size: forms.length,
    text,
    value: <T>(v: T): T => (forms.length === 0 ? v : (walk(v) as T)),
  }
}

export const NO_SECRET_REDACTOR: SecretRedactor = makeSecretRedactor([])

/** The run a secret may reach: its project (null: none) and the person it works for (`runAskerOf`). */
export interface SecretRunContext {
  readonly projectId: string | null
  readonly askerId: string
}

const reachesProject = (row: SecretReachRow, projectId: string | null): boolean =>
  row.projects === "all" ||
  (projectId !== null && (row.projects as ReadonlyArray<string>).includes(projectId))

/**
 * Does `row` reach the run, and how specifically (higher wins when two share a name)?
 * 3: the asker's own personal secret · 2: the run's project's · 1: someone's shared personal one · 0: the team's.
 * null: it does not reach the run.
 */
const rank = (row: SecretReachRow, ctx: SecretRunContext): number | null => {
  switch (row.scope) {
    case "project":
      return ctx.projectId !== null && row.projectId === ctx.projectId ? 2 : null
    case "personal":
      if (!reachesProject(row, ctx.projectId)) return null
      if (row.ownerId === ctx.askerId) return 3
      return row.shared ? 1 : null
    case "workspace":
      return reachesProject(row, ctx.projectId) ? 0 : null
  }
}

/** The secrets that reach a run, one per name (the most specific: own personal › project › shared › team). */
export const secretsReaching = <R extends SecretReachRow>(
  rows: ReadonlyArray<R>,
  ctx: SecretRunContext,
): ReadonlyMap<string, R> => {
  const best = new Map<string, { row: R; rank: number }>()
  for (const row of rows) {
    const r = rank(row, ctx)
    if (r === null) continue
    const prev = best.get(row.name)
    if (
      !prev ||
      r > prev.rank ||
      (r === prev.rank && `${row.ownerId}:${row.projectId}` < `${prev.row.ownerId}:${prev.row.projectId}`)
    )
      best.set(row.name, { row, rank: r })
  }
  return new Map([...best].sort(([a], [b]) => (a < b ? -1 : 1)).map(([k, v]) => [k, v.row]))
}

/** Why `name` does not reach the run (null: it does). The first useful hint: someone could share it, or list the project. */
export const secretUnmet = (
  rows: ReadonlyArray<SecretReachRow>,
  name: string,
  ctx: SecretRunContext,
): { readonly why: "not_added" | "not_shared" | "not_in_project"; readonly ownerId?: string } | null => {
  const named = rows.filter((r) => r.name === name)
  if (named.some((r) => rank(r, ctx) !== null)) return null
  const unshared = named.find((r) => r.scope === "personal" && !r.shared && r.ownerId !== ctx.askerId)
  if (unshared) return { why: "not_shared", ownerId: unshared.ownerId }
  if (named.some((r) => r.scope === "workspace" || r.scope === "personal")) return { why: "not_in_project" }
  return { why: "not_added" }
}
